/**
 * useNatalReading — TanStack Query hook for natal-chart readings.
 *
 * Without theme: GET /api/v1/me/natal-reading          → free portrait
 * With theme:    GET /api/v1/me/natal-reading?theme=X  → paid deep-dive
 *
 * 402 responses are surfaced as paymentRequired=true (not thrown as errors),
 * so the component can show a PaymentSheet without entering isError state.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export type NatalReadingResponse =
  | { ready: true; markdown: string }
  | { ready: false; reason: 'chart_not_ready' | 'llm_error' };

export type PaidTheme = 'love' | 'career' | 'purpose';

interface PaymentRequiredInfo {
  sku: string;
  stars: number;
  params: { theme: PaidTheme };
}

interface UseNatalReadingResult {
  data: NatalReadingResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /** True when API returned 402 — user must pay before reading is available. */
  paymentRequired: boolean;
  /** Stars cost from the 402 response (25 per theme, or 0 if no 402). */
  paymentStars: number;
  paymentInfo: PaymentRequiredInfo | null;
  refetch: () => void;
}

export function useNatalReading(
  enabled: boolean,
  theme?: PaidTheme,
  /** When true, poll every 2.5s until the reading is ready — used right after a
   *  paid purchase while the settle handler generates the row server-side. */
  poll = false,
): UseNatalReadingResult {
  const jwt = useAuth((s) => s.jwt);
  const { i18n } = useTranslation();

  const url = theme
    ? `/api/v1/me/natal-reading?theme=${theme}`
    : '/api/v1/me/natal-reading';

  const query = useQuery<NatalReadingResponse, Error & { paymentRequired?: boolean; paymentInfo?: PaymentRequiredInfo }>({
    queryKey: ['natal-reading', i18n.language, theme ?? 'portrait'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch(url, jwt);

      if (res.status === 402) {
        const body = await res.json().catch(() => ({})) as Partial<PaymentRequiredInfo & { error: string }>;
        const err = new Error('payment_required') as Error & { paymentRequired: boolean; paymentInfo: PaymentRequiredInfo };
        err.paymentRequired = true;
        err.paymentInfo = {
          sku: body.sku ?? 'natal_theme',
          stars: body.stars ?? 25,
          params: { theme: (body.params?.theme ?? theme ?? 'love') as PaidTheme },
        };
        throw err;
      }

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`natal_reading_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as NatalReadingResponse;
    },
    enabled: !!jwt && enabled,
    staleTime: 5 * 60_000,
    retry: false,
    // After purchase: poll until the settle handler has written the row.
    refetchInterval: (q): number | false => {
      if (!poll) return false;
      const d = q.state.data as NatalReadingResponse | undefined;
      return d?.ready === true ? false : 2500;
    },
  });

  const errTyped = query.error as (Error & { paymentRequired?: boolean; paymentInfo?: PaymentRequiredInfo }) | null;
  const paymentRequired = errTyped?.paymentRequired === true;
  const paymentInfo = errTyped?.paymentInfo ?? null;

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError && !paymentRequired,
    paymentRequired,
    paymentStars: paymentInfo?.stars ?? 0,
    paymentInfo,
    refetch: query.refetch,
  };
}
